import { useScrollReveal } from "@/hooks/useScrollReveal";

interface CompletionRingProps {
  progress: number; // 0-100
  size?: "sm" | "md" | "lg";
  color?: "primary" | "emerald" | "amber" | "red" | "accent" | "purple";
  label?: string;
  sublabel?: string;
  showValue?: boolean;
  animate?: boolean;
}

const SIZE_MAP = {
  sm: { ring: 48, stroke: 4, font: "text-sm" },
  md: { ring: 64, stroke: 5, font: "text-lg" },
  lg: { ring: 80, stroke: 6, font: "text-xl" },
};

const COLOR_MAP = {
  primary: { track: "text-primary-200", fill: "text-primary-500" },
  emerald: { track: "text-emerald-200", fill: "text-emerald-500" },
  amber: { track: "text-amber-200", fill: "text-amber-500" },
  red: { track: "text-red-200", fill: "text-red-500" },
  accent: { track: "text-accent-200", fill: "text-accent-500" },
  purple: { track: "text-purple-200", fill: "text-purple-500" },
};

export default function CompletionRing({
  progress, size = "md", color = "primary", label, sublabel, showValue = true, animate = true,
}: CompletionRingProps) {
  const ref = useScrollReveal<HTMLDivElement>({
    selector: ".ring-anim",
    staggerDelay: 0,
    threshold: 0.2,
  });

  const dim = SIZE_MAP[size];
  const col = COLOR_MAP[color];
  const radius = (dim.ring - dim.stroke * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;
  const clampedOffset = Math.min(circumference, Math.max(0, offset));

  return (
    <div className={`flex flex-col items-center gap-1.5 ${animate ? "ring-anim scroll-reveal" : ""}`} ref={animate ? ref : undefined}>
      <div className="relative" style={{ width: dim.ring, height: dim.ring }}>
        <svg width={dim.ring} height={dim.ring} className="-rotate-90">
          <circle
            cx={dim.ring / 2}
            cy={dim.ring / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={dim.stroke}
            className={`${col.track} transition-colors duration-500`}
          />
          <circle
            cx={dim.ring / 2}
            cy={dim.ring / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={dim.stroke}
            strokeLinecap="round"
            className={`${col.fill} transition-all duration-1000 ease-out-expo`}
            strokeDasharray={circumference}
            strokeDashoffset={clampedOffset}
            style={{ transitionDelay: "300ms" }}
          />
        </svg>
        {showValue && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`font-bold ${dim.font} text-foreground-900`}>
              {Math.round(progress)}%
            </span>
          </div>
        )}
      </div>
      {label && <span className="text-xs font-semibold text-foreground-700 text-center leading-tight">{label}</span>}
      {sublabel && <span className="text-[10px] text-foreground-400 text-center leading-tight">{sublabel}</span>}
    </div>
  );
}